import React, { useState } from 'react'
import { BigNumber } from 'bignumber.js'
import useIsMobile from '../../hooks/useIsMobile'
import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { SwitchIcon } from '../../svgIcons/SwitchIcon'
import { numFormat } from '../../blockchainUtils/math'
import { getToken } from '../../model/tokens/helper'
import { Network } from '../../services/token.service'
import { SwapQuote } from '../../atoms'

function SlippageIndicator(props: {
    token1: string
    token1Amount: string
    token2: string
    token2Amount: string
    network: Network
    currentRatio: number
    forwardSwap: boolean
    fee: number
    slippage: number
    isMaxBalance: boolean
    isMinSpent: boolean
    lastQuote: SwapQuote
}) {
    const {
        token1,
        token1Amount,
        token2,
        token2Amount,
        network,
        currentRatio,
        slippage,
        forwardSwap = true,
        fee = 10000,
        isMaxBalance = false,
        isMinSpent = false,
        lastQuote
    } = props
    const [priceDirection, setPriceDirection] = useState(false)
    const isMobile = useIsMobile()

    function getImpactColor(priceImpact: string) {
        const impactChange = Number(numFormat(priceImpact, false))
        if (impactChange > 5) return '#FF4D4D'
        if (impactChange > 3) return '#FFBC6C'
        return 'white'
    }

    const calcToken2MinAmount = () => {
        if (isMinSpent || isMaxBalance) return Number(token2Amount) - (Number(token2Amount) * slippage) / 100
        let slippageCalc = parseFloat(slippage.toString()) / 100
        return BigNumber(token1Amount).times(BigNumber(1).plus(BigNumber(slippageCalc)))
    }

    function getMinText() {
        if (isMinSpent) return isMobile ? 'Min' : 'Min received '
        if (isMaxBalance) return isMobile ? 'Max' : 'Max Spent '
        return isMobile ? 'Max' : 'Max spent '
    }

    if (token1 && token2 && token1Amount && !BigNumber(token1Amount).isZero() && token2Amount && !BigNumber(token2Amount).isZero()) {
        let minAmount = calcToken2MinAmount()
        const token1Obj = getToken(network, token1)
        const token2Obj = getToken(network, token2)
        if (!token1Obj || !token2Obj) return <div></div>
        let token1Symbol = token1Obj.symbol
        let token2Symbol = token2Obj.symbol
        let floatSlippage = parseFloat(slippage.toString())
        if (!currentRatio) return <div></div>

        // const newOtherTokenRatio = forwardSwap ? Number(token2Amount) / Number(token1Amount) : Number(token1Amount) / Number(token2Amount)
        // let priceImpact = Math.abs((1 - currentRatio / newOtherTokenRatio) * 100) - Number(fee || 0) / 10000
        // if (priceImpact < 0) priceImpact = priceImpact * -1
        if (!lastQuote) return <div></div>
        if (!lastQuote.poolPriceAfter || !lastQuote.poolPriceBefore) return <div></div>
        const priceImpact = BigNumber(lastQuote.poolPriceAfter).minus(lastQuote.poolPriceBefore).dividedBy(lastQuote.poolPriceBefore).multipliedBy(100).abs()

        const displayPrice1 = (
            <>
                1 {token1Symbol} = {numFormat((Number(token2Amount) / Number(token1Amount)).toString())} {token2Symbol}
            </>
        )

        const displayPrice2 = (
            <>
                1 {token2Symbol} = {numFormat((Number(token1Amount) / Number(token2Amount)).toString())} {token1Symbol}
            </>
        )

        return (
            <div>
                <div className='flex items-center bg-transparent h-[56px] mb-4 mt-4 px-4' style={{ border: '1px solid #0C3F6D', borderRadius: '8px' }}>
                    <div className='cursor-pointer w-full flex items-center' onClick={() => setPriceDirection(!priceDirection)}>
                        <span className='text-[16px] lg:text-def font-bold text-white'>{priceDirection ? displayPrice2 : displayPrice1}</span>
                        <div className='hover:text-[#6F6E8F] ml-3'>
                            <SwitchIcon className='w-[18px] h-[18px]' />
                        </div>
                    </div>
                </div>

                <div className='bg-transparen p-4 mb-4 mt-4 text-white' style={{ border: '1px solid #0C3F6D', borderRadius: '8px' }}>
                    <div className='flex justify-between w-full mb-2'>
                        <span className='relative text-white flex items-center'>
                            {getMinText()}
                            (Slippage=
                            {floatSlippage.toFixed(2)}
                            %)
                            <div className='ml-2 group inline-flex'>
                                <HelptipIcon className='w-5 h-5' />
                                <span className='w-[280px] left-0 group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                    {'Your transaction will revert if there is a large unfavorable price movement before it is confirmed.'}
                                </span>
                            </div>
                        </span>
                        <span className='font-bold text-4'>{numFormat(minAmount ? minAmount.toString() : '0')}</span>
                    </div>

                    <div className='flex justify-between w-full'>
                        <div className='relative flex items-center'>
                            <span className='text-4 block'>Price impact</span>
                            <div className='ml-2 group flex'>
                                <HelptipIcon className='w-5 h-5' />
                                <span className='w-[280px] left-0 group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                    {'The difference between the market price and estimated price due to trade size.'}
                                </span>
                            </div>
                        </div>
                        <div className={`font-bold text-[16px] `} style={{ color: getImpactColor(priceImpact.toString()) }}>
                            {numFormat(priceImpact.toString(), false)}%
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    return <div></div>
}

export default SlippageIndicator
